import React, { useRef, useState, useEffect, useCallback } from "react";
import { Button } from "./Button";
import { twMerge } from "tailwind-merge";
import { Loader, CircleArrowLeft, CircleArrowRight } from "lucide-react";
import Tooltip from "./Tooltip";
import {
  InformationCircleIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/solid";

export interface TabOption {
  label: string | React.ReactNode;
  value: string;
  loading?: boolean;
  disabled?: string; // Provide reason for disabling tab as a string for the tooltip
  url?: string;
}

export interface TabsProps {
  tabs: TabOption[];
  selected: TabOption | null;
  onSelect: (tab: TabOption) => void;
  vertical?: boolean;
  className?: string;
  variant?: "primary" | "secondary" | "tertiary";
  align?: "left" | "center";
  enableScroll?: boolean;
  overflowFadeClassName?: string;
}

// Reusable loading spinner component
const LoadingSpinner = () => (
  <Loader
    data-testid="loading-spinner"
    className="animate-spin size-4"
    style={{ animationDuration: "2s" }}
  />
);

const DisabledTabTooltip = ({ disabled }: { disabled: string }) => (
  <div className="flex items-center justify-center">
    <Tooltip message={disabled}>
      <InformationCircleIcon className="size-4 text-chariot-text-secondary" />
    </Tooltip>
  </div>
);

export const Tabs: React.FC<TabsProps> = ({
  tabs,
  selected,
  onSelect,
  vertical,
  className,
  variant = "primary",
  align = "center",
  enableScroll = false,
}) => {
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const checkScroll = useCallback(() => {
    if (!enableScroll || !scrollContainerRef.current) {
      setShowLeftArrow(false);
      setShowRightArrow(false);
      return;
    }
    const { scrollLeft, scrollWidth, clientWidth } =
      scrollContainerRef.current;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1); // -1 for potential subpixel issues
  }, [enableScroll]);

  useEffect(() => {
    if (enableScroll && scrollContainerRef.current) {
      checkScroll();
      const container = scrollContainerRef.current;
      container.addEventListener("scroll", checkScroll);
      window.addEventListener("resize", checkScroll);

      // Check scroll on tabs change or initial mount
      const observer = new MutationObserver(checkScroll);
      observer.observe(container, { childList: true, subtree: true });


      return () => {
        container.removeEventListener("scroll", checkScroll);
        window.removeEventListener("resize", checkScroll);
        observer.disconnect();
      };
    }
  }, [tabs, enableScroll, checkScroll]);


  const scroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const { clientWidth, scrollWidth, scrollLeft } = scrollContainerRef.current;
      const scrollAmount = clientWidth * 0.4;

      // Calculate the effective scroll amount, respecting boundaries
      let effectiveScrollAmount = 0;
      if (direction === "left") {
        effectiveScrollAmount = -Math.min(scrollAmount, scrollLeft); // Don't scroll beyond 0
      } else {
        effectiveScrollAmount = Math.min(scrollAmount, scrollWidth - scrollLeft - clientWidth); // Don't scroll beyond max
      }

      // Only scroll if there's an amount to scroll by
      if (Math.abs(effectiveScrollAmount) > 0) {
        scrollContainerRef.current.scrollBy({
          left: effectiveScrollAmount,
          behavior: "smooth",
        });
      }
    }
  };

  const getTabStyles = (isSelected: boolean) => {
    if (variant === "secondary") {
      return twMerge(
        isSelected
          ? "bg-chariot-background-secondary text-chariot-text-primary hover:bg-chariot-background-secondary"
          : "text-chariot-text-secondary hover:text-chariot-text-primary hover:bg-chariot-background-secondary"
      );
    }

    if (variant === "tertiary") {
      return twMerge(
        isSelected
          ? "rounded-none border-chariot-brand-primary text-chariot-brand-primary"
          : "border-transparent text-chariot-text-secondary hover:border-transparent hover:text-chariot-text-primary"
      );
    }

    return twMerge(
      isSelected
        ? "border border-chariot-text-primary bg-chariot-background-secondary text-chariot-text-primary"
        : "border border-chariot-stroke text-chariot-text-secondary hover:border-chariot-text-primary hover:text-chariot-text-primary"
    );
  };

  const getIcon = (tab: TabOption) => {
    if (tab.disabled) {
      return <DisabledTabTooltip disabled={tab.disabled} />;
    } else if (tab.loading) {
      return <LoadingSpinner />;
    } else if (tab.url) {
      return (
        <ArrowTopRightOnSquareIcon className="size-4 text-chariot-text-secondary" />
      );
    }
  };

  return (
    <div
      className={twMerge(
        "relative w-full",
        !enableScroll && "gap-4 flex-nowrap",
        vertical && "flex-col",
        variant === "secondary" && !enableScroll && "border-none",
        variant === "tertiary" &&
          !vertical &&
          !enableScroll &&
          "gap-0 border-0 border-b-2 border-chariot-stroke",
        className
      )}
      data-testid="tabs"
    >
      {enableScroll && (
        <div
          className={twMerge(
            "absolute left-0 top-0 bottom-0 w-12 pointer-events-none z-10",
            "bg-linear-to-r from-chariot-background-primary to-transparent",
            "transition-opacity duration-200 ease-in-out",
            showLeftArrow ? "opacity-100" : "opacity-0"
          )}
        />
      )}
      {enableScroll && showLeftArrow && (
        <Button
          styleType="icon"
          size="sm"
          label={
            <CircleArrowLeft
              className="size-6 text-chariot-text-secondary"
            />
          }
          onClick={() => {
            scroll("left");
          }}
          className={twMerge(
            "absolute left-0 top-1/2 -translate-y-1/2 z-20",
            "w-[31px] h-[31px] flex items-center justify-center"
          )}
          aria-label="Scroll tabs left"
        />
      )}
      <div
        ref={scrollContainerRef}
        className={twMerge(
          "flex flex-nowrap overflow-x-scroll no-scrollbar will-change-scroll",
          enableScroll && "gap-2",
          !enableScroll && "w-full gap-4",
          vertical && "flex-col w-full",
          variant === "secondary" && enableScroll && "border-none",
          variant === "tertiary" &&
            !vertical &&
            enableScroll &&
            "gap-0 border-0 border-b-2 border-chariot-stroke"
        )}
      >
        {tabs.map((tab, index) => (
          <Button
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            key={tab.value}
            styleType="tertiary"
            size="xl"
            label={tab.label}
            onClick={() => {
              if (tab.url) {
                window.open(tab.url, "_blank", "noopener,noreferrer");
              }
              onSelect(tab);
            }}
            className={twMerge(
              "flex-1 whitespace-nowrap",
              vertical && "w-full",
              "transition-colors duration-200 font-bold",
              align === "left" ? "justify-start" : "justify-center",
              variant === "secondary" && "border-none py-2 text-sm font-medium",
              variant === "tertiary" && "border-0 border-b-4 text-sm font-bold",
              variant === "tertiary" && !vertical && "size-fit flex-none",
              selected && getTabStyles(selected.value === tab.value),
              enableScroll && "flex-none"
            )}
            endIcon={getIcon(tab)}
            disabled={tab.disabled !== undefined}
          />
        ))}
      </div>
      {enableScroll && (
        <div
          className={twMerge(
            "absolute right-0 top-0 bottom-0 w-12 pointer-events-none z-10",
            "bg-linear-to-l from-chariot-background-primary to-transparent",
            "transition-opacity duration-200 ease-in-out",
            showRightArrow ? "opacity-100" : "opacity-0"
          )}
        />
      )}
      {enableScroll && showRightArrow && (
        <Button
          styleType="icon"
          size="sm"
          label={
            <CircleArrowRight
              className="size-6 text-chariot-text-secondary"
            />
          }
          onClick={() => {
            scroll("right");
          }}
          className={twMerge(
            "absolute right-0 top-1/2 -translate-y-1/2 z-20",
            "w-[31px] h-[31px] flex items-center justify-center"
          )}
          aria-label="Scroll tabs right"
        />
      )}
    </div>
  );
};
